<template>
  <modal
    :show="show"
    :title="state === 'edit' ? 'Preview and edit links' : edit ? 'Update product' : 'Add product from link'"
    :class="`import-products ${state === 'edit' ? 'gray-bg' : ''}`"
    @close="close"
  >
    <template v-if="['', 'loading'].includes(state)">
      <div class="p-a-4">
        <p class="card-content-title black">
          <strong>Link list</strong>
          <span>One product link per row</span>
        </p>
        <div class="links-editor">
          <textarea class="lines" :value="linkLines" ref="lines"></textarea>
          <textarea class="links" v-model="linkText" ref="editor" v-on:scroll.passive="scrollEditor"></textarea>
        </div>
        <Button class="f-w" icon="eye-24" :disabled="!canPreviewLinks || state === 'loading'" :loading="state === 'loading'" @click="fetchLinks">Preview and edit links</Button>
      </div>
    </template>
    <template v-else>
      <div class="p-a-4">
        <div class="imported-item" :data-has-error="product && (!product.image_url || !product.title || product.failed || !product.url)" v-for="(product, index) in scraped" :key="`${linksKey}-${index}`" ref="product">
          <template v-if="editingProduct === index">
            <div class="f-w">
              <template v-if="!edit">
                <div class="p-a-4">
                  <div class="d-f">
                    <FormInput type="text" class="with-button f-w" v-model="refetchUrl" />
                    <Button icon="download-document-export-24" class="with-input" @click="refetch(index)" :loading="refetchState === 'loading'">Refetch</Button>
                  </div>
                </div>
                <div class="line"></div>
              </template>
              <div class="p-a-4">
                <div class="d-f">
                  <div class="f-1 p-r-4">
                    <p class="card-content-title black">
                      <strong>Image</strong>
                    </p>
                    <media-upload
                      size="h/200"
                      :title="$t('settings.liveshopping.image')"
                      :chessboard="false"
                      :multiple="false"
                      :overwrite="true"
                      :data="{ force: true, images: product.image_url ? [product.image_url] : [] }"
                      :error="!scraped[editingProduct].image_url ? 'Image is required': ''"
                      :disabled="isNotCustom"
                      @images="setProductImage($event)"
                    />
                  </div>
                  <div class="f-1-5">
                    <p class="card-content-title black">
                      <strong>Title</strong>
                    </p>
                    <FormInput
                      type="text"
                      class="with-button f-w"
                      v-model="scraped[editingProduct].title"
                      :error="scraped[editingProduct].title === '' ? 'Title is required' : ''"
                      :resetable="scraped[editingProduct].has_overrides && scraped[editingProduct].title !== scraped[editingProduct].original_values.title"
                      @reset="scraped[editingProduct].title = scraped[editingProduct].original_values.title"
                    />
                    <p class="card-content-title black m-t-2">
                      <strong>ID</strong>
                    </p>
                    <FormInput type="text" class="f-w mono" v-model="scraped[editingProduct].product_id" :disabled="isNotCustom" />
                    <p class="card-content-title black m-t-2">
                      <strong>Price</strong>
                    </p>
                    <div class="d-f">
                      <form-input
                        type="number"
                        class="f-1 m-r-1"
                        :value="parseFloat(scraped[editingProduct].price)"
                        :stepper="true"
                        :min="1"
                        :step="1"
                        :max="99999999"
                        @input="scraped[editingProduct].price = parseFloat($event)"
                        ref="priceInput"
                        :disabled="isNotCustom"
                      />
                      <form-dropdown
                        class="f-1 m-l-1"
                        :items="productCurrencies"
                        :placeholder="$t('common.currency')"
                        :forceUp="true"
                        @input="scraped[editingProduct].currency = $event.value"
                        :disabled="isNotCustom"
                      />
                    </div>
                  </div>
                </div>
                <p class="card-content-title black m-t-2">
                  <strong>Link</strong>
                </p>
                <FormInput type="text" class="with-button f-w" v-model="scraped[editingProduct].url" :error="scraped[editingProduct].url === '' ? 'URL is required' : ''" :disabled="isNotCustom" />
                <p class="card-content-title black m-t-2">
                  <strong>Description</strong>
                </p>
                <textarea :value="scraped[editingProduct].description" @input="scraped[editingProduct].description = $event.target.value" v-bind:disabled="isNotCustom" ref="descriptionTextarea"></textarea>
                <template v-if="product.attributes">
                  <!--<p class="card-content-title black m-t-2">
                    <strong>Attributes</strong>
                  </p>
                  -->
                  <div class="d-f fd-c">
                    <template v-for="(attribute, index) in scraped[editingProduct].original_attributes">
                      <p class="card-content-title black m-t-2" :key="`t-${index}`">
                        <strong>Attribute name - {{ attribute }}</strong>
                      </p>
                      <FormInput
                        type="text"
                        class="with-button f-w"
                        v-model="scraped[editingProduct].attributes[index]"
                        :key="`i-${index}`"
                        :error="scraped[editingProduct].attributes[index] === '' ? 'Attribute needs to be named' : ''"
                        :resetable="scraped[editingProduct].attributes[index] !== scraped[editingProduct].original_attributes[index]"
                        @reset="resetAttribute(index)"
                      />
                    </template>
                  </div>
                </template>
              </div>
              <div class="p-x-4 p-b-4" v-if="!edit">
                <Button class="f-w" @click="editProduct(null)">Done</Button>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="image">
              <template v-if="product.failed">
                <i class="icon-warning" />
              </template>
              <template v-else>
                <img :src="product.image_url.substring(0, 4) === 'http' ? product.image_url : `https://${fqdn}.streamify.io/images/h/85/${product.image_url}`" v-if="product.image_url" />
                <span v-else>No image</span>
              </template>
            </div>
            <div class="info elippsiss">
              <template v-if="product.failed">
                <h2>{{ product.failed_reason }}</h2>
              </template>
              <template v-else>
                <span v-if="product.product_id">{{ product.product_id }}</span>
                <h2 :class="`elippsiss ${!product.title ? 'gray' : ''}`">{{ product.title || 'No title' }}</h2>
                <p class="description elippsiss">{{ product.description }}</p>
                <p class="price" v-if="typeof product.price !== 'undefined'">{{ product.price }} {{ product.currency.toUpperCase() }}</p>
              </template>
            </div>
            <div class="actions">
              <div class="blue" @click="editProduct(index)"><i class="icon-edit-pen2" /></div>
              <div class="red" @click="removeProduct(index)"><i class="icon-trash-24" /></div>
            </div>
          </template>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <template v-if="state === 'edit'">
        <div class="d-f f-w f-g-8">
          <Button class="f-w" :disabled="!canAddProducts" @click="canAddProducts ? addProducts() : null" :loading="addState === 'adding'">
            <template v-if="canAddProducts">
              Add product{{ scraped.length > 1 ? 's' : '' }}
            </template>
            <template v-else>
              One or more products needs some attention
            </template>
          </Button>
        </div>
      </template>
      <template v-else-if="state === 'product'">
        <div class="d-f f-w f-g-8">
          <template v-if="isNotCustom">
            <Button class="f-w" :disabled="!canUpdateProduct" @click="canUpdateProduct ? updateRealProduct() : null" :loading="updateState === 'updating'">Update product</Button>
          </template>
          <template v-else>
            <Button class="f-w" :disabled="!canUpdateProduct" @click="canUpdateProduct ? addProducts() : null">Update product</Button>
          </template>
          <Button class="f-w outline red" icon="trash1" @click="deleteProduct">Remove product</Button>
        </div>
      </template>
    </template>
  </modal>
</template>

<script>

import Vue from 'vue';
import MediaUpload from '@/components/MediaUpload.vue';

export default {
  props: {
    show: {
      type: Boolean,
    },
    edit: {
      type: Object,
    },
    isNotCustom: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    MediaUpload,
  },
  computed: {
    /**
     * Broadcast - The current broadcast in the store
     * @return {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Can add products
     */
    canAddProducts() {
      if (this.scraped.filter(p => p.failed).length > 0) {
        return false;
      }

      for (let i = 0; i < this.scraped.length; i += 1) {
        if (this.scraped[i].title === '' || this.scraped[i].image_url === '') {
          return false;
        }
      }

      if (this.addState === 'adding') {
        return false;
      }

      return true;
    },
    canUpdateProduct() {
      return !(typeof this.scraped[0].title === 'undefined' || this.scraped[0].title === '' || typeof this.scraped[0].image_url === 'undefined' || this.scraped[0].image_url === '' || typeof this.scraped[0].url === 'undefined' || this.scraped[0].url === '');
    },
    canPreviewLinks() {
      return this.linkText.trim() !== '';
    },
    productCurrencies: {
      get() {
        if (this.editingProduct === null) {
          return [];
        }

        const {
          currency,
        } = this.scraped[this.editingProduct];

        if (!currency) {
          return this.availableCurrencies.map(c => ({ value: c, string: c.toUpperCase() }));
        }

        return this.availableCurrencies.map(c => ({ value: c, string: c.toUpperCase(), selected: c.toLowerCase() === currency.toLowerCase() }));
      },
      set(value) {
        this.scraped[this.editingProduct].currency = value;
      },
    },
  },
  methods: {
    resetAttribute(index) {
      Vue.set(this.scraped[0].attributes, index, this.scraped[0].attributes_map[this.scraped[0].attributes[index]]);
    },
    /**
     * Close the modal
     * @returns {Void}
     */
    close() {
      if (this.addState !== '') {
        return;
      }

      this.$emit('close', true);
      this.scraped = [];
      this.state = '';
      this.linkLines = 1;
      this.linkText = '';
    },
    /**
     * Fetch links and turn them into products
     * @returns {Void}
     */
    async fetchLinks() {
      // Maybe also validate link?
      const links = this.linkText.split(/\n/).reduce((a, b) => (b !== '' ? [...a, b] : a), []);

      this.state = 'loading';
      // TODO: Use array of products later
      const scraped = await this.$store.dispatch('Products/scrapeProduct', links);

      if (!scraped) {
        this.state = '';
        return;
      }

      this.scraped = scraped.map(l => ({ ...l, price: l.price / 100 }));

      this.state = 'edit';
    },
    /**
     * Refetch a link
     * @param {Number} key
     * @returns {Void}
     */
    async refetch(key) {
      this.refetchState = 'loading';
      const product = await this.$store.dispatch('Products/scrapeProduct', [this.refetchUrl]);
      this.refetchState = '';
      // eslint-disable-next-line
      Vue.set(this.scraped, key, {
        ...product[0],
        price: product[0].price / 100,
      });
      this.linksKey += 1;
    },
    /**
     * Set new image in scraped product
     * @param {Array} images
     * @returns {Void}
     */
    setProductImage(images) {
      if (images.length === 0) {
        this.scraped[this.editingProduct].image_url = '';
      } else {
        this.scraped[this.editingProduct].image_url = images[0].value;
      }
    },
    /**
     * Mark product for editing
     * @param {Number|Null} key
     * @returns {Void}
     */
    editProduct(key) {
      this.editingProduct = key;
      this.refetchUrl = key === null ? '' : this.scraped[this.editingProduct].url;

      if (key !== null) {
        if (typeof this.scraped[this.editingProduct].failed !== 'undefined') {
          Vue.delete(this.scraped[this.editingProduct], 'failed');
        }
        if (typeof this.scraped[this.editingProduct].title === 'undefined') {
          Vue.set(this.scraped[this.editingProduct], 'title', '');
        }
        if (typeof this.scraped[this.editingProduct].description === 'undefined') {
          Vue.set(this.scraped[this.editingProduct], 'description', '');
        }
        if (typeof this.scraped[this.editingProduct].image_url === 'undefined') {
          Vue.set(this.scraped[this.editingProduct], 'image_url', '');
        }
        if (typeof this.scraped[this.editingProduct].price === 'undefined') {
          Vue.set(this.scraped[this.editingProduct], 'price', '');
        }
        if (typeof this.scraped[this.editingProduct].currency === 'undefined') {
          Vue.set(this.scraped[this.editingProduct], 'currency', '');
        }

        this.$nextTick(() => {
          this.$refs.product[key].parentElement.parentElement.scrollTop = this.$refs.product[key].offsetTop - 84;
        });
      }
    },
    /**
     * Remove product from list
     * @param {Number} key
     * @returns {Void}
     */
    removeProduct(key) {
      this.editProduct(null);
      this.refetchUrl = '';
      this.scraped.splice(key, 1);
      if (this.scraped.length === 0) {
        this.state = '';
      }
    },
    async deleteProduct() {
      const willDelete = await swal({
        title: this.$t('errors.are_you_sure'), text: 'This will remove product from broadcast', icon: 'warning', buttons: true, dangerMode: true,
      });

      if (!willDelete) {
        return;
      }

      const bool = await this.$store.dispatch('Products/removeProductFromBroadcast', { id: this.broadcast.id, arr: [this.edit.id] });
      if (!bool) {
        swal({ title: 'An error occured', text: 'Could not remove product', icon: 'error' });
        return;
      }

      this.$nextTick(() => {
        this.$store.dispatch('Broadcast/update', { id: this.broadcast.id });
        this.$store.dispatch('Products/listBroadcastProducts', this.broadcast.id);
      });

      this.$nextTick(() => {
        this.close();
      });
    },
    goToProductWithError() {
      for (let i = 0; i < this.scraped.length; i += 1) {
        if (typeof this.scraped[i].title === 'undefined' || this.scraped[i].title === '' || typeof this.scraped[i].image_url === 'undefined' || this.scraped[i].image_url === '' || typeof this.scraped[i].url === 'undefined' || this.scraped[i].url === '' || typeof this.scraped[i].failed !== 'undefined') {
          this.editProduct(i);
          break;
        }
      }
    },
    async updateRealProduct() {
      const {
        broadcast: {
          id,
        },
      } = this;

      this.updateState = 'updating';

      const payload = {
        product_id: this.scraped[0].product_id,
        title: this.scraped[0].title,
      };

      const attributes = {};
      this.scraped[0].original_attributes.forEach((attribute, index) => {
        attributes[attribute] = this.scraped[0].attributes[index];
      });
      payload.attributes = attributes;

      const result = await this.$store.dispatch('Products/updateProductOnBroadcast', {
        id,
        payload,
      });

      if (!result) {
        this.updateState = '';
        swal({ title: this.$t('errors.error_occured'), text: 'There was an error while updating your product.', icon: 'error' });

        this.$nextTick(() => {
          this.$store.dispatch('Broadcast/update', { id: this.broadcast.id, data: {} });
          this.$store.dispatch('Products/listBroadcastProducts', id);
        });
        return;
      }

      this.updateState = '';
      this.$emit('close', true);

      this.$nextTick(() => {
        this.$store.dispatch('Broadcast/update', { id: this.broadcast.id, data: {} });
        this.$store.dispatch('Products/listBroadcastProducts', id);
      });
    },
    async addProducts() {
      const {
        broadcast: {
          id,
        },
        scraped,
      } = this;

      this.addState = 'adding';

      const products = JSON.parse(JSON.stringify(scraped.filter(s => !s.failed)));
      products.forEach((product) => {
        if (product.price === 0 || product.price === '') {
          delete product.currency;
          delete product.price;
        } else {
          product.price = product.price * 100;
        }
      });

      const result = await this.$store.dispatch('Products/addScrapedProductToBroadcast', {
        id,
        arr: products,
      });

      if (!result) {
        this.addState = '';
        swal({ title: this.$t('errors.error_occured'), text: 'There was an error while importing your products.', icon: 'error' });

        this.$nextTick(() => {
          this.$store.dispatch('Broadcast/update', { id: this.broadcast.id, data: {} });
          this.$store.dispatch('Products/listBroadcastProducts', id);
        });
        return;
      }

      this.addState = '';
      this.$emit('close', true);

      this.$nextTick(() => {
        this.$store.dispatch('Broadcast/update', { id: this.broadcast.id, data: {} });
        this.$store.dispatch('Products/listBroadcastProducts', id);
      });
    },
    scrollEditor() {
      this.$refs.lines.scrollTop = this.$refs.editor.scrollTop;
      this.$refs.lines.scrollLeft = this.$refs.editor.scrollLeft;
    },
  },
  watch: {
    edit: {
      immediate: true,
      handler(product) {
        if (product) {
          const p = JSON.parse(JSON.stringify(product));

          /*
          if (p.attribute_map) {
            const maps = Object.keys(p.attribute_map);
            p.attributes.forEach((attr, index) => {
              if (maps.includes(attr)) {
                p.attributes[index] = p.attribute_map[attr];
              }
            });
          }
          */

          const original_attributes = JSON.parse(JSON.stringify(p.attributes));
          if (p.attributes_map) {
            Object.keys(p.attributes_map).forEach((attr) => {
              const attrIndex = original_attributes.findIndex(a => a === attr);
              if (attrIndex >= 0) {
                original_attributes[attrIndex] = p.attributes_map[attr];
              }
            });
          }

          this.scraped = [{ ...p, price: product.price ? product.price / 100 : product.price, original_attributes }];
          this.state = 'product';
          this.editingProduct = 0;
        }
      },
    },
    linkText: {
      immediate: true,
      handler(links) {
        const lines = links.split(/\n/).length;
        const numbers = [];
        for (let i = 1; i <= lines; i += 1) {
          numbers.push(i < 10 ? `0${i}` : `${i}`);
        }
        this.linkLines = numbers.join("\n");
      },
    },
  },
  data() {
    return {
      linkLines: 1,
      linkText: '',
      linksKey: 0,
      scraped: [],
      editingProduct: null,
      state: '', // none, loading, edit
      addState: '',
      updateState: '',
      refetchState: '',
    };
  },
};
</script>

<style lang="scss">
.import-products {
  .line {
    background-color: #EEF0F9;
    height: 1px;
    width: 100%;
  }

  .links-editor {
    border: 1px solid #D7D9E0;
    border-radius: 4px;
    box-shadow: inset 0 1px 2px 0 rgba(0,0,0,0.10);
    display: flex;
    margin-bottom: 15px;

    textarea {
      border: 0;
      line-height: 22px;
      font-family: 'Fira Mono', monospace;
      resize: none;
      padding: 6px;
      white-space: nowrap;
    }

    textarea.lines {
      background-color: #efefef;
      outline: none;
      pointer-events: none;
      overflow-y: hidden;
      text-align: right;
      white-space: pre-wrap;
      width: 40px;

      &:focus {
        outline: none;
      }
    }

    textarea.links {
      background-color: white;
      white-space: pre;
      overflow-wrap: normal;
      overflow-x: scroll;
      width: 100%;
    }
  }

  .imported-item {
    background: #FFFFFF;
    border: 1px solid transparent;
    box-shadow: 0 2px 4px 0 rgba(165,179,194,0.20), 0 0 1px 0 rgba(165,179,194,0.50);
    border-radius: 4px;
    display: flex;
    transition: border-color .3s;

    &[data-has-error="true"] {
      border-color: #FF4848;
      box-shadow: inset 0 1px 2px 0 rgba(0,0,0,0.10);
    }

    .media-upload {
      .card-content {
        background-color: unset;
        border-radius: unset;
        box-shadow: unset;
        & > div {
          padding: 0;
        }
      }
    }

    textarea {
      background: #FFFFFF;
      border: 1px solid #D7D9E0;
      border-radius: 0;
      box-shadow: inset 0 1px 2px 0 rgb(0 0 0 / 10%);
      padding: 8px;
      min-height: 100px;
      resize: none;

      &:focus {
        border-color: var(--color-cta-blue);
      }

      &:disabled {
        background-color: #f3f3f3;
      }
    }

    .image-error {
      font-style: italic;
      font-size: 13px;
      line-height: 18px;
      margin-top: 2px;
      color: #FF1717;
    }

    .image {
      align-items: center;
      justify-content: center;
      display: flex;
      min-width: 85px;
      height: 85px;
      width: 85px;
      border-radius: 4px 0 0 4px;
      overflow: hidden;

      img {
        object-fit: cover;
        min-width: 85px;
        height: 85px;
        width: 85px;
      }

      i {
        font-size: 36px;
        color: #878787;
      }

      span {
        font-family: 'Fira Mono', monospace;
        font-size: 10px;
        color: #727479;
        text-transform: uppercase;
      }
    }

    .info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 5px 20px;
      width: 100%;

      span {
        font-family: 'Fira Mono', monospace;
        font-size: 10px;
        line-height: 12px;
        color: #727479;
      }

      h2 {
        font-size: 15px;
        font-weight: 500;

        &.gray {
          color: #727479
        }
      }

      .description {
        color: #727479;
        font-size: 12px;
      }

      .price {
        font-size: 14px;
      }
    }

    .actions {
      display: flex;

      & > div {
        border-left: 1px solid #EEF0F9;
        align-items: center;
        display: flex;
        justify-content: center;
        padding: 0 12px;

        &:hover {
          cursor: pointer;
        }

        i {
          font-size: 24px;
        }

        &.red {
          color: #EB4646;
        }

        &.blue {
          color: #0060FF;
        }
      }
    }

    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
}
</style>
